import { useState } from 'react';
import type { RewardConfig, RewardLevelRowView, RewardSnapshot } from '@contracts';
import { RewardIcon } from '@ui/shared/icons/RewardIcon';
import { IconPickerPopover } from '@ui/desktop/rewards/IconPickerPopover';

/**
 * `LevelArcRow` (settings-rewards.md §11.3) — the condensed `LevelArcList`
 * row for phone. The desktop row anatomy (icon tile + name + right-aligned
 * threshold) already fits a narrow column — it was never table-shaped — so
 * this carries it forward with sizing-only adjustments: the icon tile
 * shrinks 34px -> 28px (matching the XP-sources rows above it for visual
 * consistency scrolling from one list into the next), and the current-level
 * row's "you are here" annotation drops to its own second line rather than
 * fighting for inline width.
 */
export function LevelArcRow({
  row,
  snapshot,
  editing,
  config,
  onChange,
}: {
  readonly row: RewardLevelRowView;
  readonly snapshot: RewardSnapshot;
  readonly editing: boolean;
  readonly config: RewardConfig;
  readonly onChange: (next: RewardConfig) => void;
}) {
  const isCurrent = row.level === snapshot.level;
  const ariaLabel = isCurrent
    ? `Level ${row.level}, ${row.name}, ${row.totalXpRequired} XP, you are here, ${snapshot.xpTotal} XP now`
    : `Level ${row.level}, ${row.name}, ${row.totalXpRequired} XP`;

  return (
    <div
      role="listitem"
      aria-label={ariaLabel}
      style={{
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: isCurrent ? 'var(--surface-hover)' : 'transparent',
        border: isCurrent ? '1px solid var(--border-strong)' : '1px solid transparent',
        marginTop: 'var(--space-1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          className="num"
          style={{ width: '20px', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', flex: 'none' }}
        >
          {row.level}
        </span>

        {editing ? (
          <EditableIconTile
            iconId={row.iconId}
            current={isCurrent}
            onChange={(iconId) => onChange(withLevelIdentity(config, row.level, { iconId }))}
          />
        ) : (
          <span
            aria-hidden="true"
            style={{
              width: '28px',
              height: '28px',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 'none',
              background: isCurrent ? 'var(--hue-primary-vivid)' : 'var(--surface-hover)',
            }}
          >
            <RewardIcon iconId={row.iconId} size={16} color={isCurrent ? 'var(--text-on-accent)' : 'var(--text-secondary)'} />
          </span>
        )}

        {editing ? (
          <input
            value={row.name}
            maxLength={24}
            onChange={(e) => onChange(withLevelIdentity(config, row.level, { name: e.target.value }))}
            style={{
              flex: 1,
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontFamily: 'var(--font-family)',
            }}
          />
        ) : (
          <span style={{ flex: 1, fontSize: 'var(--type-body-size)', fontWeight: 500, color: 'var(--text-primary)' }}>{row.name}</span>
        )}

        {editing && row.level > 1 ? (
          <input
            type="number"
            className="num"
            value={row.totalXpRequired}
            min={0}
            onChange={(e) => onChange(withThreshold(config, row.level, Math.max(0, Number(e.target.value) || 0)))}
            style={{
              width: '80px',
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-2)',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-sunken)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              fontFamily: 'var(--font-family)',
              textAlign: 'right',
              flex: 'none',
            }}
          />
        ) : (
          <span className="num" style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', flex: 'none' }}>
            {row.totalXpRequired.toLocaleString()} XP
          </span>
        )}
      </div>
      {isCurrent && !editing && (
        <div
          className="num"
          style={{
            marginTop: 'var(--space-1)',
            paddingLeft: 'calc(20px + 28px + var(--space-3) + var(--space-3))',
            fontSize: 'var(--type-caption-size)',
            color: 'var(--text-secondary)',
          }}
        >
          — you are here · {snapshot.xpTotal.toLocaleString()} XP now
        </div>
      )}
    </div>
  );
}

function EditableIconTile({
  iconId,
  current,
  onChange,
}: {
  readonly iconId: string;
  readonly current: boolean;
  readonly onChange: (iconId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative', flex: 'none' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Change icon, currently ${iconId}`}
        style={{
          width: '28px',
          height: '28px',
          borderRadius: 'var(--radius-sm)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: current ? 'var(--hue-primary-vivid)' : 'var(--surface-hover)',
          border: '1px solid var(--border-strong)',
        }}
      >
        <RewardIcon iconId={iconId} size={16} color={current ? 'var(--text-on-accent)' : 'var(--text-secondary)'} />
      </button>
      {open && (
        <IconPickerPopover
          selected={iconId}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
}

function withLevelIdentity(
  config: RewardConfig,
  level: number,
  changes: { readonly name?: string; readonly iconId?: string },
): RewardConfig {
  return {
    ...config,
    levelIdentities: config.levelIdentities.map((identity) =>
      identity.level === level ? { ...identity, ...changes } : identity,
    ),
  };
}

function withThreshold(config: RewardConfig, level: number, nextValue: number): RewardConfig {
  const index = level - 2;
  const thresholds = config.levelCurve.thresholds.map((t, i) => (i === index ? nextValue : t));
  return { ...config, levelCurve: { thresholds } };
}
